// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Divider,
  Flex,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { createReference, formatDate, formatHumanName } from '@medplum/core';
import type { Communication, Task } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  DME_THERAPY_LABELS,
  DME_THERAPY_TO_HCPCS,
  formatPressureSummary,
  getDmeTherapyTypeFromMedicationRequest,
  readPressureFromMedicationRequest,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { IconCircleCheck, IconCopy, IconExternalLink, IconLock, IconMessage } from '@tabler/icons-react';
import { type JSX, type ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  DME_ORDER_REFERENCE_OUTPUT_LABEL,
  DME_TASK_BUSINESS_STATUSES,
  getDmeTaskBusinessStatus,
  PARACHUTE_PORTAL_URL,
  withDmeTaskBusinessStatus,
} from '../../constants/dme';
import { useDmeTaskDetails } from '../../hooks/useDmeTaskDetails';
import {
  formatAhiValue,
  getDiagnosisIcd10Coding,
  getDmeVendor,
  getDmeVendorInstructions,
  getPatientShippingAddress,
} from '../../utils/dme-task';
import { showErrorNotification } from '../../utils/notifications';
import { getPlanName } from '../../utils/patient-coverage';
import { getPractitionerNpi } from '../../utils/patient-referring-provider';
import {
  findCurrentTaskPrescriptionDocument,
  getPrescriptionDocumentInputReference,
} from '../../utils/prescription-pdf';
import { withTenantScope } from '../../utils/tenant';
import { CancelDmeOrderModal } from './CancelDmeOrderModal';
import { MarkDmeSubmittedModal } from './MarkDmeSubmittedModal';

const NOT_RECORDED = 'Not recorded';

interface DmeTaskDetailProps {
  task: Task;
  onTaskUpdated?: (task: Task) => void;
}

/**
 * Read-only detail view for a submit-dme-order Task. Surfaces every piece of
 * information a provider needs to place the order in Parachute manually, plus
 * the "Mark as submitted" action.
 */
export function DmeTaskDetail({ task: taskProp, onTaskUpdated }: DmeTaskDetailProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [task, setTask] = useState<Task>(taskProp);
  const [submitModalOpened, setSubmitModalOpened] = useState(false);
  const [cancelModalOpened, setCancelModalOpened] = useState(false);
  const [blockedNote, setBlockedNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [signedPdfUrl, setSignedPdfUrl] = useState<string>();

  useEffect(() => {
    setTask(taskProp);
  }, [taskProp]);

  const prescriptionInputReference = getPrescriptionDocumentInputReference(taskProp)?.reference;
  useEffect(() => {
    let objectUrl: string | undefined;
    let disposed = false;
    const medicationRequestId = taskProp.focus?.reference?.startsWith('MedicationRequest/')
      ? taskProp.focus.reference.split('/')[1]
      : undefined;
    if (!medicationRequestId && !prescriptionInputReference) {
      return;
    }
    findCurrentTaskPrescriptionDocument(medplum, prescriptionInputReference, medicationRequestId)
      .then(async (document) => {
        const attachment = document?.content?.[0]?.attachment;
        if (attachment?.data) {
          setSignedPdfUrl(`data:${attachment.contentType ?? 'application/pdf'};base64,${attachment.data}`);
        } else if (attachment?.url?.startsWith('Binary/')) {
          const blob = await medplum.download(attachment.url);
          if (disposed) {
            return;
          }
          objectUrl = URL.createObjectURL(blob);
          setSignedPdfUrl(objectUrl);
        } else {
          setSignedPdfUrl(attachment?.url);
        }
      })
      .catch((error: unknown) => {
        Sentry.captureException(error);
        setSignedPdfUrl(undefined);
      });
    return () => {
      disposed = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [medplum, prescriptionInputReference, taskProp.focus?.reference]);

  const { details, loading, error } = useDmeTaskDetails(task);

  if (loading) {
    return (
      <Center h="100%" data-testid="dme-task-loading">
        <Loader size="md" />
      </Center>
    );
  }

  const { medicationRequest, encounter, patient, orderingProvider, coverage, diagnosis, ahiObservation } = details;

  const therapy = medicationRequest ? getDmeTherapyTypeFromMedicationRequest(medicationRequest) : undefined;
  const deviceType = therapy ? `${DME_THERAPY_LABELS[therapy]} (${DME_THERAPY_TO_HCPCS[therapy].code})` : NOT_RECORDED;
  // A known device without a coded pressure (supplies only, mask change) has no pressure line at all, matching the PDF.
  const orderedPressure = medicationRequest && readPressureFromMedicationRequest(medicationRequest);
  let pressure: string | undefined = NOT_RECORDED;
  if (therapy) {
    pressure = orderedPressure ? formatPressureSummary(therapy, orderedPressure) : undefined;
  }

  const icd10 = getDiagnosisIcd10Coding(diagnosis);
  const diagnosisLabel = icd10 ? `${icd10.display ?? 'Diagnosis'} (${icd10.code})` : NOT_RECORDED;
  const ahi = formatAhiValue(ahiObservation) || NOT_RECORDED;

  const patientName = patient?.name?.[0] ? formatHumanName(patient.name[0]) : NOT_RECORDED;
  const dob = patient?.birthDate ? formatDate(patient.birthDate) : NOT_RECORDED;
  const memberId = coverage?.subscriberId || NOT_RECORDED;
  const planName = (coverage && getPlanName(coverage)) || NOT_RECORDED;
  const shippingAddress = getPatientShippingAddress(patient) || NOT_RECORDED;

  const providerName = orderingProvider?.name?.[0] ? formatHumanName(orderingProvider.name[0]) : NOT_RECORDED;
  const providerNpi = getPractitionerNpi(orderingProvider) || NOT_RECORDED;

  const createdDate = task.authoredOn ? formatDate(task.authoredOn) : NOT_RECORDED;
  const referenceNumber = task.output?.find((o) => o.type?.text === DME_ORDER_REFERENCE_OUTPUT_LABEL)?.valueString;
  const vendor = getDmeVendor(medicationRequest);
  const businessStatus = getDmeTaskBusinessStatus(task);
  const canClaim = !task.owner?.reference && task.status !== 'completed' && task.status !== 'cancelled';
  const canBlock = task.status !== 'completed' && task.status !== 'cancelled';

  const writeTask = async (next: Task): Promise<void> => {
    setSaving(true);
    try {
      const updated = await medplum.updateResource(next, ifMatchOptions(task));
      setTask(updated);
      onTaskUpdated?.(updated);
    } catch (error) {
      showErrorNotification(error);
    } finally {
      setSaving(false);
    }
  };

  const claimTask = async (): Promise<void> => {
    if (!profile?.id) {
      return;
    }
    await writeTask(
      withDmeTaskBusinessStatus(
        { ...task, owner: { reference: `${profile.resourceType}/${profile.id}` }, status: 'in-progress' },
        DME_TASK_BUSINESS_STATUSES.claimed
      )
    );
  };

  const blockTask = async (): Promise<void> => {
    const reason = blockedNote.trim();
    if (!reason) {
      return;
    }
    await writeTask(
      withDmeTaskBusinessStatus(
        { ...task, status: 'on-hold', statusReason: { text: reason }, note: [...(task.note ?? []), { text: reason }] },
        DME_TASK_BUSINESS_STATUSES.blocked
      )
    );
    if (task.requester?.reference && task.for?.reference) {
      const communication: Communication = withTenantScope<Communication>({
        resourceType: 'Communication',
        status: 'in-progress',
        subject: { reference: task.for.reference } as Communication['subject'],
        recipient: [{ reference: task.requester.reference } as NonNullable<Communication['recipient']>[number]],
        sender: profile?.id ? createReference(profile) : undefined,
        ...(task.encounter ? { encounter: task.encounter } : {}),
        payload: [{ contentString: `DME order needs clinician input: ${reason}` }],
      });
      await medplum.createResource(communication);
    }
    setBlockedNote('');
  };

  const changeBusinessStatus = async (value: string | null): Promise<void> => {
    if (!value) {
      return;
    }
    const statusByBusinessStatus: Record<string, Task['status']> = {
      [DME_TASK_BUSINESS_STATUSES.new]: 'ready',
      [DME_TASK_BUSINESS_STATUSES.pendingDocumentation]: 'on-hold',
      [DME_TASK_BUSINESS_STATUSES.claimed]: 'in-progress',
      [DME_TASK_BUSINESS_STATUSES.submitted]: 'in-progress',
      [DME_TASK_BUSINESS_STATUSES.acknowledged]: 'in-progress',
      [DME_TASK_BUSINESS_STATUSES.blocked]: 'on-hold',
      [DME_TASK_BUSINESS_STATUSES.closed]: 'completed',
    };
    await writeTask(
      withDmeTaskBusinessStatus(
        { ...task, status: statusByBusinessStatus[value] ?? task.status },
        value as (typeof DME_TASK_BUSINESS_STATUSES)[keyof typeof DME_TASK_BUSINESS_STATUSES]
      )
    );
  };

  const copy = async (value: string): Promise<void> => {
    await navigator.clipboard.writeText(value);
  };

  const copyOrderBlock = async (): Promise<void> => {
    await copy(
      [
        `Patient: ${patientName}`,
        `DOB: ${dob}`,
        `Member ID: ${memberId}`,
        `Plan: ${planName}`,
        `Shipping address: ${shippingAddress}`,
        `Device: ${deviceType}`,
        ...(pressure ? [`Pressure: ${pressure}`] : []),
        `Diagnosis: ${diagnosisLabel}`,
        `AHI: ${ahi}`,
        `Ordering provider: ${providerName}`,
        `NPI: ${providerNpi}`,
        `Vendor: ${vendor}`,
      ].join('\n')
    );
  };

  const handleTaskWritten = (updatedTask: Task): void => {
    setTask(updatedTask);
    onTaskUpdated?.(updatedTask);
  };

  return (
    <Flex direction="column" h="100%">
      <Paper h="100%">
        <Flex justify="space-between" align="center" p="lg" h={72}>
          <Text size="xl" fw={600} lh={1.2}>
            DME Order Submission
          </Text>
          <Group>
            {canClaim && (
              <Button leftSection={<IconLock size={16} />} onClick={() => claimTask()} loading={saving}>
                Claim task
              </Button>
            )}
            {vendor === 'Parachute' && (
              <Button
                component="a"
                href={PARACHUTE_PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                leftSection={<IconExternalLink size={16} />}
              >
                Go to Parachute
              </Button>
            )}
          </Group>
        </Flex>

        <ScrollArea w="100%" h="calc(100% - 72px)" p="lg">
          {error && (
            <Alert color="red" mb="lg" data-testid="dme-task-error">
              Failed to load some order details. {error.message}
            </Alert>
          )}

          <Stack gap="lg">
            <Alert color="blue" title={`${vendor} instructions`} icon={<IconMessage size={16} />}>
              {getDmeVendorInstructions(vendor)}
            </Alert>
            <Button variant="light" leftSection={<IconCopy size={16} />} onClick={() => copyOrderBlock()}>
              Copy order details
            </Button>
            {signedPdfUrl && (
              <Stack gap="xs">
                <Button component="a" href={signedPdfUrl} download="dme-order-prescription.pdf" variant="light">
                  Download signed PDF
                </Button>
                <iframe
                  title="Signed DME prescription"
                  src={signedPdfUrl}
                  style={{ width: '100%', height: 420, border: 0 }}
                />
              </Stack>
            )}
            <Section title="Patient">
              <Field
                label="Name"
                value={
                  patient?.id ? (
                    <Anchor component={Link} to={`/Patient/${patient.id}`}>
                      {patientName}
                    </Anchor>
                  ) : (
                    patientName
                  )
                }
              />
              <Field label="Date of birth" value={dob} onCopy={() => copy(dob)} />
              <Field label="Member ID" value={memberId} onCopy={() => copy(memberId)} />
              <Field label="Plan" value={planName} onCopy={() => copy(planName)} />
              <Field label="Shipping address" value={shippingAddress} onCopy={() => copy(shippingAddress)} />
            </Section>

            <Divider />

            <Section title="Device">
              <Field label="Device type" value={deviceType} onCopy={() => copy(deviceType)} />
              {pressure && <Field label="Pressure settings" value={pressure} onCopy={() => copy(pressure)} />}
            </Section>

            <Divider />

            <Section title="Diagnosis">
              <Field label="OSA diagnosis" value={diagnosisLabel} onCopy={() => copy(diagnosisLabel)} />
              <Field label="AHI" value={ahi} onCopy={() => copy(ahi)} />
            </Section>

            <Divider />

            <Section title="Ordering provider">
              <Field label="Provider" value={providerName} onCopy={() => copy(providerName)} />
              <Field label="NPI" value={providerNpi} onCopy={() => copy(providerNpi)} />
              <Field label="Order created" value={createdDate} onCopy={() => copy(createdDate)} />
            </Section>

            <Divider />

            <Section title="Linked records">
              <Field
                label="Encounter"
                value={
                  encounter?.id ? (
                    <Anchor component={Link} to={`/Encounter/${encounter.id}`}>
                      View encounter note
                    </Anchor>
                  ) : (
                    NOT_RECORDED
                  )
                }
              />
              <Field
                label="Treatment"
                value={
                  medicationRequest?.id ? (
                    <Anchor component={Link} to={`/MedicationRequest/${medicationRequest.id}`}>
                      View treatment record
                    </Anchor>
                  ) : (
                    NOT_RECORDED
                  )
                }
              />
            </Section>

            <Divider />

            <Section title="Order status">
              <OrderStatus
                task={task}
                vendor={vendor}
                businessStatus={businessStatus}
                onBusinessStatusChange={changeBusinessStatus}
                referenceNumber={referenceNumber}
                onMarkSubmitted={() => setSubmitModalOpened(true)}
                onCancelOrder={() => setCancelModalOpened(true)}
              />
            </Section>
            {canBlock && (
              <Section title="Needs clinician input">
                <Textarea
                  value={blockedNote}
                  onChange={(event) => setBlockedNote(event.currentTarget.value)}
                  placeholder="Describe what clinician must address"
                />
                <Button variant="outline" onClick={() => blockTask()} disabled={!blockedNote.trim()} loading={saving}>
                  Put on hold and notify clinician
                </Button>
              </Section>
            )}
          </Stack>
        </ScrollArea>
      </Paper>

      <MarkDmeSubmittedModal
        task={task}
        patient={patient}
        vendor={vendor}
        opened={submitModalOpened}
        onClose={() => setSubmitModalOpened(false)}
        onSubmitted={handleTaskWritten}
      />

      <CancelDmeOrderModal
        task={task}
        opened={cancelModalOpened}
        onClose={() => setCancelModalOpened(false)}
        onCancelled={handleTaskWritten}
      />
    </Flex>
  );
}

function ifMatchOptions(task: Task): { headers: { 'If-Match': string } } | undefined {
  return task.meta?.versionId ? { headers: { 'If-Match': `W/\"${task.meta.versionId}\"` } } : undefined;
}

function OrderStatus({
  task,
  vendor,
  businessStatus,
  onBusinessStatusChange,
  referenceNumber,
  onMarkSubmitted,
  onCancelOrder,
}: {
  task: Task;
  vendor: string;
  businessStatus: string;
  onBusinessStatusChange: (value: string | null) => Promise<void>;
  referenceNumber: string | undefined;
  onMarkSubmitted: () => void;
  onCancelOrder: () => void;
}): JSX.Element {
  if (task.status === 'completed') {
    return (
      <Stack gap={6}>
        <Badge color="green" variant="light" leftSection={<IconCircleCheck size={14} />}>
          {task.status === 'completed' ? 'Submitted to Parachute' : businessStatus}
        </Badge>
        {referenceNumber && <Field label="Parachute reference" value={referenceNumber} />}
      </Stack>
    );
  }
  if (task.status === 'cancelled') {
    return (
      <Stack gap={6}>
        <Badge color="red" variant="light">
          Order cancelled
        </Badge>
        {task.statusReason?.text && <Field label="Cancellation reason" value={task.statusReason.text} />}
      </Stack>
    );
  }
  return (
    <Stack gap="sm">
      <Select
        label="Business status"
        value={businessStatus}
        data={Object.values(DME_TASK_BUSINESS_STATUSES)}
        onChange={onBusinessStatusChange}
        allowDeselect={false}
      />
      <Group>
        {businessStatus !== DME_TASK_BUSINESS_STATUSES.submitted &&
          businessStatus !== DME_TASK_BUSINESS_STATUSES.acknowledged && (
            <Button onClick={onMarkSubmitted}>Mark as submitted to {vendor}</Button>
          )}
        <Button variant="outline" color="red" onClick={onCancelOrder}>
          Cancel order
        </Button>
      </Group>
    </Stack>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <Stack gap="xs">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">
        {title}
      </Text>
      {children}
    </Stack>
  );
}

function Field({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: ReactNode;
  onCopy?: () => Promise<void>;
}): JSX.Element {
  return (
    <Stack gap={2}>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      {typeof value === 'string' ? (
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" c="dimmed">
            {value}
          </Text>
          {onCopy && value !== NOT_RECORDED && (
            <Button variant="subtle" size="compact-xs" aria-label={`Copy ${label}`} onClick={() => onCopy()}>
              <IconCopy size={14} />
            </Button>
          )}
        </Group>
      ) : (
        value
      )}
    </Stack>
  );
}
